"use client";

import { useState } from "react";
import { Input, Button, Select, SelectItem, Slider, Checkbox, Card, CardBody } from "@nextui-org/react";
import { Search, Filter, X } from "lucide-react";
import { ProductFilters } from "@/types/woocommerce";
import { WooCommerceCategory } from "@/types/woocommerce";

interface ProductFiltersProps {
  filters: ProductFilters;
  onFiltersChange: (filters: ProductFilters) => void;
  categories: WooCommerceCategory[];
}

const sortOptions = [
  { key: "date", label: "Newest First" },
  { key: "price", label: "Price: Low to High" },
  { key: "price-desc", label: "Price: High to Low" },
  { key: "popularity", label: "Popularity" },
  { key: "rating", label: "Rating" },
];

// Filter options matching the screenshot
const gorivoOptions = [
  { key: "diesel", label: "Diesel" },
  { key: "gas", label: "Gas" },
];

const frekvencijaOptions = [
  { key: "50-hz", label: "50 Hz" },
  { key: "60-hz", label: "60 Hz" },
];

const fazaOptions = [
  { key: "single-phase", label: "Single phase" },
  { key: "three-phase", label: "Three phase" },
];

const emisijaOptions = [
  { key: "cumple-epa", label: "Cumple EPA" },
  { key: "non-certified", label: "non certified" },
  { key: "non-emission", label: "Non emission" },
  { key: "stage-2", label: "Stage 2" },
  { key: "stage-3a", label: "Stage 3A" },
  { key: "stage-5", label: "Stage 5" },
];

export function ProductFiltersComponent({ filters, onFiltersChange, categories }: ProductFiltersProps) {
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState<ProductFilters>(filters);

  // Initialize arrays if undefined
  const gorivo = localFilters.gorivo || [];
  const frekvencija = localFilters.frekvencija || [];
  const faza = localFilters.faza || [];
  const emisija = localFilters.emisija || [];

  const handleSearchChange = (value: string) => {
    const newFilters = { ...localFilters, search: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSortChange = (value: string) => {
    const newFilters = { ...localFilters, orderby: value as ProductFilters["orderby"] };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleAttributeChange = (
    attribute: "gorivo" | "frekvencija" | "faza" | "emisija",
    value: string,
    checked: boolean
  ) => {
    const currentValues = localFilters[attribute] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter((v) => v !== value);

    const newFilters = { ...localFilters, [attribute]: newValues };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSnagaChange = (value: number | number[]) => {
    const values = Array.isArray(value) ? value : [6, value as number];
    const newFilters = {
      ...localFilters,
      minSnaga: values[0],
      maxSnaga: values[1],
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const cleared: ProductFilters = {};
    setLocalFilters(cleared);
    onFiltersChange(cleared);
  };

  const hasActiveFilters = Object.values(localFilters).some((v) =>
    Array.isArray(v) ? v.length > 0 : v !== undefined && v !== ""
  );

  // Render checkbox group
  const renderCheckboxGroup = (
    title: string,
    options: { key: string; label: string }[],
    attribute: "gorivo" | "frekvencija" | "faza" | "emisija",
    currentValues: string[]
  ) => (
    <div className="mb-6">
      <h3 className="text-danger text-lg font-medium mb-3">{title}</h3>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {options.map((option) => (
          <Checkbox
            key={option.key}
            isSelected={currentValues.includes(option.key)}
            onValueChange={(checked) =>
              handleAttributeChange(attribute, option.key, checked)
            }
            size="sm"
          >
            {option.label}
          </Checkbox>
        ))}
      </div>
    </div>
  );

  // Sidebar content
  const SidebarContent = () => (
    <div className="space-y-4">
      {/* Search in sidebar */}
      <Input
        type="text"
        placeholder="Search products..."
        value={localFilters.search || ""}
        onValueChange={handleSearchChange}
        startContent={<Search className="w-4 h-4 text-default-400" />}
        size="sm"
      />

      {/* Sort dropdown */}
      <Select
        label="Sort By"
        placeholder="Newest First"
        selectedKeys={localFilters.orderby ? [localFilters.orderby] : []}
        onChange={(e) => handleSortChange(e.target.value)}
        size="sm"
      >
        {sortOptions.map((option) => (
          <SelectItem key={option.key} value={option.key}>
            {option.label}
          </SelectItem>
        ))}
      </Select>

      <hr className="border-default-200" />

      {/* Gorivo (Fuel) */}
      {renderCheckboxGroup("Gorivo", gorivoOptions, "gorivo", gorivo)}

      <hr className="border-default-200" />

      {/* Frekvencija (Frequency) */}
      {renderCheckboxGroup("Frekvencija", frekvencijaOptions, "frekvencija", frekvencija)}

      <hr className="border-default-200" />

      {/* Faza (Phase) */}
      {renderCheckboxGroup("Faza", fazaOptions, "faza", faza)}

      <hr className="border-default-200" />

      {/* Emisija (Emission) */}
      {renderCheckboxGroup("Emisija", emisijaOptions, "emisija", emisija)}

      <hr className="border-default-200" />

      {/* Opseg snage (Power Range) */}
      <div className="mb-6">
        <h3 className="text-danger text-lg font-medium mb-3">Opseg snage</h3>
        <Slider
          size="sm"
          step={10}
          minValue={6}
          maxValue={3300}
          value={[
            localFilters.minSnaga || 6,
            localFilters.maxSnaga || 3300,
          ]}
          onChange={(value) => handleSnagaChange(value as number[])}
          className="w-full"
          formatOptions={{ style: "decimal", maximumFractionDigits: 0 }}
        />
        <div className="flex justify-between text-xs text-default-500 mt-2">
          <span>6</span>
          <span>830</span>
          <span>1.653</span>
          <span>2.477</span>
          <span>3.300</span>
        </div>
      </div>

      {hasActiveFilters && (
        <Button
          variant="flat"
          color="danger"
          size="sm"
          onPress={clearFilters}
          className="w-full"
          endContent={<X className="w-4 h-4" />}
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile filter toggle */}
      <div className="lg:hidden mb-4">
        <Button
          variant="flat"
          fullWidth
          onPress={() => setIsMobileExpanded(!isMobileExpanded)}
          endContent={<Filter className="w-4 h-4" />}
        >
          Filters
        </Button>
      </div>

      {/* Mobile expanded view */}
      {isMobileExpanded && (
        <Card className="lg:hidden mb-4">
          <CardBody>
            <SidebarContent />
          </CardBody>
        </Card>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <SidebarContent />
      </div>
    </>
  );
}
